import { getApiErrorCode, getApiErrorMessage } from "./messages";
import {
  MSG_GITHUB_BUSY,
  MSG_GITHUB_TIMEOUT,
  MSG_NETWORK,
  MSG_SERVER,
  MSG_TIMEOUT,
  MSG_UNAVAILABLE,
} from "./constants";

export const ERROR_KINDS = {
  NETWORK: "network",
  TIMEOUT: "timeout",
  SERVER: "server",
  UNAVAILABLE: "unavailable",
  AUTH: "auth",
  FORBIDDEN: "forbidden",
  QUOTA: "quota",
  VALIDATION: "validation",
  CLIENT: "client",
  UNKNOWN: "unknown",
};

const TIMEOUT_CODES = new Set(["ECONNABORTED", "ETIMEDOUT"]);
const NETWORK_CODES = new Set([
  "ERR_NETWORK",
  "ECONNREFUSED",
  "ERR_CANCELED",
]);

export function isGithubApiUrl(url = "") {
  return String(url).includes("/github");
}

/**
 * True when the classified error should open the global retry/support panel.
 */
export function isGlobalBoundaryError(classified) {
  if (!classified) return false;
  if (isGithubApiUrl(classified.url)) return false;
  return (
    classified.kind === ERROR_KINDS.NETWORK ||
    classified.kind === ERROR_KINDS.TIMEOUT ||
    classified.kind === ERROR_KINDS.SERVER ||
    classified.kind === ERROR_KINDS.UNAVAILABLE
  );
}

/**
 * Classify an Axios/fetch/AuthServiceError into a stable shape for the UI.
 *
 * Helper so interceptors, SSE helpers, and the global boundary share one
 * taxonomy instead of per-screen string matching.
 */
export function classifyApiError(error, overrides = {}) {
  const status =
    overrides.status !== undefined && overrides.status !== null
      ? overrides.status
      : error?.response?.status ?? error?.status ?? null;
  const codeFromBody = getApiErrorCode(error);
  const codeFromError =
    typeof error?.code === "string" ? error.code : "";
  const code = overrides.code || codeFromBody || codeFromError || "";
  const requestId =
    overrides.requestId ||
    error?.response?.headers?.["x-request-id"] ||
    error?.response?.data?.request_id ||
    "";
  const method = (
    overrides.method ||
    error?.config?.method ||
    ""
  ).toUpperCase();
  const url = overrides.url || error?.config?.url || "";

  let kind = ERROR_KINDS.UNKNOWN;
  let message = getApiErrorMessage(error, MSG_SERVER);
  let showSupport = false;
  let retryable = false;

  const axiosCode = typeof error?.code === "string" ? error.code : "";
  const isTimeout =
    TIMEOUT_CODES.has(axiosCode) ||
    /timeout/i.test(String(error?.message || "")) ||
    status === 408 ||
    status === 504;

  if (!error?.response && (error?.isAxiosError || error?.request)) {
    if (isTimeout || TIMEOUT_CODES.has(axiosCode)) {
      kind = ERROR_KINDS.TIMEOUT;
      message = MSG_TIMEOUT;
      showSupport = true;
      retryable = true;
    } else if (!NETWORK_CODES.has(axiosCode) && error?.name === "AuthServiceError") {
      kind = ERROR_KINDS.CLIENT;
      message = getApiErrorMessage(error, message);
    } else {
      kind = ERROR_KINDS.NETWORK;
      message = MSG_NETWORK;
      showSupport = true;
      retryable = true;
    }
  } else if (isTimeout) {
    kind = ERROR_KINDS.TIMEOUT;
    message = MSG_TIMEOUT;
    showSupport = true;
    retryable = true;
  } else if (status === 401) {
    kind = ERROR_KINDS.AUTH;
  } else if (status === 402 || error?.isLimitExpired || code === "LIMIT_EXPIRED") {
    kind = ERROR_KINDS.QUOTA;
  } else if (status === 403) {
    kind = ERROR_KINDS.FORBIDDEN;
  } else if (status === 422 || status === 400) {
    kind = ERROR_KINDS.VALIDATION;
  } else if (status === 429) {
    kind = ERROR_KINDS.UNAVAILABLE;
    message = MSG_UNAVAILABLE;
    showSupport = true;
    retryable = true;
  } else if (status === 502 || status === 503) {
    kind = ERROR_KINDS.UNAVAILABLE;
    message = MSG_UNAVAILABLE;
    showSupport = true;
    retryable = true;
  } else if (status != null && status >= 500) {
    kind = ERROR_KINDS.SERVER;
    message = MSG_SERVER;
    showSupport = true;
    retryable = true;
  } else if (status != null && status >= 400) {
    kind = ERROR_KINDS.CLIENT;
  }

  if (
    isGithubApiUrl(url) &&
    (kind === ERROR_KINDS.TIMEOUT ||
      kind === ERROR_KINDS.UNAVAILABLE ||
      kind === ERROR_KINDS.SERVER ||
      kind === ERROR_KINDS.NETWORK)
  ) {
    showSupport = false;
    message =
      kind === ERROR_KINDS.TIMEOUT ? MSG_GITHUB_TIMEOUT : MSG_GITHUB_BUSY;
  }

  if (overrides.kind) {
    kind = overrides.kind;
  }
  if (overrides.message) {
    message = overrides.message;
  }
  if (overrides.showSupport != null) {
    showSupport = overrides.showSupport;
  }
  if (overrides.retryable != null) {
    retryable = overrides.retryable;
  }

  return {
    kind,
    message,
    status,
    code,
    requestId,
    method,
    url,
    showSupport,
    retryable,
    source: overrides.source || "api",
    raw: error,
  };
}
